import { Component, EventEmitter, Output, inject, OnInit, Input, OnDestroy } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import {
  IPrelimInvoiceResponse,
  IParkerProfileDetail,
  ICompaniesShortList,
  IParkerActiveListForInvoiceGeneration,
  IInvoiceSystemGenerationDefaults,
  IInvoiceGenerationJobStatus,
} from 'src/app/shared/interfaces';
import { DataService } from 'src/app/core/services/data/data.service';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { IDefaultGbGbCreationPrelimRequest } from 'src/app/shared/interfaces';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { DialogAlertCardProcessingFeeMismatchComponent } from 'src/app/core/dialog/dialog-alert-card-processing-fee-mismatch/dialog-alert-card-processing-fee-mismatch.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { interval, Subscription, switchMap, takeWhile, tap, catchError, of, timer } from 'rxjs';

@Component({
  selector: 'app-gb-default-configuration',
  standalone: true,
  imports: [MatTabsModule, MatCheckboxModule, FormsModule, DatePipe, MatFormFieldModule, MatInputModule],
  templateUrl: './gb-default-configuration.component.html',
  styleUrl: './gb-default-configuration.component.scss',
})
export class GbDefaultConfigurationComponent implements OnInit, OnDestroy {
  displayPrelimInvoices: boolean = false;
  displayPreliminaryInvoices: boolean = false;
  prelimInvoiceList: IPrelimInvoiceResponse[] = [];
  isProcessing: boolean = false;
  invoiceLineDescription: string = '';
  parkersWithExpiredRates: IParkerProfileDetail[] = [];
  isInvoiceLineDescriptionCustomizationEnabled: boolean = false;
  companyList: ICompaniesShortList[] = [];
  parkerList: IParkerActiveListForInvoiceGeneration[] = [];
  currentSelectedCompanyId: string = '';
  selectedMonths: number = 1;
  isSelectAllParkersChecked: boolean = false;
  isParkerSelectionReadOnly: boolean = false;
  revenueDate: Date = new Date();
  dueDate: Date = new Date();
  isCurrentMonthOnly: boolean = false;
  private hasInitialized: boolean = false;
  private isResetForLotChangeScheduled: boolean = false;
  private lastCancelGenerationToken: number = 0;
  disablePrelimGeneration: boolean = false;
  invoiceSystemDefaults: IInvoiceSystemGenerationDefaults = undefined as any;

  // Background job tracking
  private currentJobId: string | null = null;
  private pollingSubscription: Subscription | null = null;
  private readonly POLL_INTERVAL_MS = 10000; // Poll every 10 seconds
  jobProgressPercentage: number = 0;
  jobStatusMessage: string = '';

  @Input()
  set systemDefaults(defaults: IInvoiceSystemGenerationDefaults) {
    this.invoiceSystemDefaults = defaults;
    if (defaults && this.hasInitialized) {
      this.scheduleResetForLotChange();
    }
  }

  @Input()
  set resetPrelimButton(_: number) {
    this.disablePrelimGeneration = false;
  }

  @Input()
  set invoiceLineCustomizationStatus(status: boolean) {
    this.isInvoiceLineDescriptionCustomizationEnabled = status;
  }

  @Input()
  set lotNumberResetToken(_: number) {
    if (!this.hasInitialized) {
      return;
    }
    this.scheduleResetForLotChange();
  }

  @Input()
  set cancelGenerationToken(token: number) {
    if (!this.hasInitialized || token === this.lastCancelGenerationToken) {
      return;
    }

    this.lastCancelGenerationToken = token;
    this.cancelGeneration();
  }

  @Output() prelimInvoiceListChange = new EventEmitter<IPrelimInvoiceResponse[]>();
  @Output() processingStateChange = new EventEmitter<boolean>();
  @Output() notifyOfErrorDuringInvoiceGeneration = new EventEmitter<string>();

  dataService = inject(DataService);
  appGeneralStateService = inject(AppGeneralStateService);
  dialog = inject(MatDialog);

  ngOnInit(): void {
    this.setRevenueDate();
    this.hasInitialized = true;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }



  onConfirmProceedGeneratingPrelimInvoices(): void {
    this.onGenerateGbDefaultPrelimInvoices();
  }

  onGenerateGbDefaultPrelimInvoices() {
    this.isProcessing = true;
    this.processingStateChange.emit(true);
    this.jobProgressPercentage = 0;
    this.jobStatusMessage = 'Submitting job...';

    const invoiceData = this.prepareDefaultPathPrelimInvoiceData();

    // Start the background job
    this.dataService.GenerateGbDefaultPreliminaryInvoices(invoiceData).subscribe({
      next: (response) => {
        // Job was queued successfully, start polling for status
        this.currentJobId = response.jobId;
        this.jobStatusMessage = response.message;
        this.startPolling(response.jobId);
      },
      error: (error) => {
        this.isProcessing = false;
        this.processingStateChange.emit(false);
        this.notifyOfErrorDuringInvoiceGeneration.emit(this.getGenerationSubmissionErrorMessage(error));
      },
    });
  }

  private getGenerationSubmissionErrorMessage(error: any): string {
    if (error?.status === 409 && typeof error.error?.message === 'string') {
      return error.error.message;
    }

    return '';
  }

  private startPolling(jobId: string): void {
    this.stopPolling();

    this.pollingSubscription = timer(0, this.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.dataService.GetInvoiceGenerationJobStatus(jobId)),
        tap((status) => {
          // 🔍 DEBUG: Log the raw status
          // console.log('🔍 Raw status object:', status);
          // console.log('🔍 Status value:', status.status);
          // console.log('🔍 Status type:', typeof status.status);
          // console.log('🔍 Is Queued?', status.status === 'Queued');
          // console.log('🔍 Is Processing?', status.status === 'Processing');

          this.jobProgressPercentage = status.progressPercentage;
          this.jobStatusMessage = status.statusMessage;
        }),
        takeWhile((status) => {
          const shouldContinue = status.status === 'Queued' || status.status === 'Processing';
          // console.log('🔍 takeWhile check:', status.status, '→ Continue?', shouldContinue);
          return shouldContinue;
        }, true),
        catchError((error) => {
          console.error('❌ Polling error:', error);
          this.handlePollingError(error);
          return of(null);
        }),
      )
      .subscribe({
        next: (status) => {
          //console.log('📬 Next called with:', status);
          if (!status) return;

          if (status.status === 'Completed') {
            this.handleJobCompleted(status);
          } else if (status.status === 'Failed') {
            this.handleJobFailed(status);
          } else if (status.status === 'Cancelled') {
            this.handleJobCancelled(status);
          }
        },
        error: (error) => {
          console.error('❌ Error in subscribe:', error);
          this.handlePollingError(error);
        },
        complete: () => {
          //console.log('🏁 Observable completed');
          this.stopPolling();
        },
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private handleJobCompleted(status: IInvoiceGenerationJobStatus): void {
    this.prelimInvoiceList = status.result || [];
    this.displayPrelimInvoices = true;
    this.displayPreliminaryInvoices = true;
    this.isProcessing = false;
    this.processingStateChange.emit(false);
    this.disablePrelimGeneration = this.prelimInvoiceList.length > 0;
    this.prelimInvoiceListChange.emit(this.prelimInvoiceList);
    this.jobProgressPercentage = 100;
    this.jobStatusMessage = 'Invoice generation completed successfully';
    this.currentJobId = null;
  }

  private handleJobFailed(status: IInvoiceGenerationJobStatus): void {
    this.isProcessing = false;
    this.processingStateChange.emit(false);
    this.notifyOfErrorDuringInvoiceGeneration.emit(
      status.errorMessage ||
        'An error occurred while generating preliminary invoices. Please try again or contact support.',
    );
    this.jobProgressPercentage = 0;
    this.jobStatusMessage = 'Job failed';
    this.currentJobId = null;
  }

  private handleJobCancelled(status: IInvoiceGenerationJobStatus): void {
    this.isProcessing = false;
    this.processingStateChange.emit(false);
    this.jobProgressPercentage = 0;
    this.jobStatusMessage = 'Job was cancelled';
    this.currentJobId = null;
  }

  private handlePollingError(error: any): void {
    this.isProcessing = false;
    this.processingStateChange.emit(false);
    this.notifyOfErrorDuringInvoiceGeneration.emit(
      'Error checking job status. The job may still be processing. Please refresh and check preliminary invoices list.',
    );
    this.stopPolling();
    this.currentJobId = null;
  }

  cancelGeneration(): void {
    // Stop polling - note that the backend job will continue running
    // This just stops the UI from checking status
    this.stopPolling();
    this.isProcessing = false;
    this.processingStateChange.emit(false);
    this.jobProgressPercentage = 0;
    this.jobStatusMessage = 'Cancelled checking job status';
    this.currentJobId = null;
  }

  setRevenueDate(): void {
    const now = new Date();
    let targetDate: Date;

    targetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    this.revenueDate = targetDate;
    this.dueDate = targetDate;
  }

  prepareDefaultPathPrelimInvoiceData(): IDefaultGbGbCreationPrelimRequest {
    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;

    const invoiceData: IDefaultGbGbCreationPrelimRequest = {
      lotNumber: this.appGeneralStateService.currentLotNumber,
      invoiceDate: this.revenueDate,
      dueDate: this.dueDate,
      userName: this.appGeneralStateService.userName,
      oneOffParker: false,
      tParkerId: '',
      gbLineDescription: this.invoiceLineDescription,
    };
    return invoiceData;
  }

  private resetForLotChange(): void {
    // Stop any active polling
    this.stopPolling();
    this.currentJobId = null;

    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;
    this.isProcessing = false;
    this.disablePrelimGeneration = false;
    this.invoiceLineDescription = '';
    this.jobProgressPercentage = 0;
    this.jobStatusMessage = '';
    this.setRevenueDate();
    this.processingStateChange.emit(false);
    this.prelimInvoiceListChange.emit([]);
  }

  private scheduleResetForLotChange(): void {
    if (this.isResetForLotChangeScheduled) {
      return;
    }

    this.isResetForLotChangeScheduled = true;
    queueMicrotask(() => {
      this.isResetForLotChangeScheduled = false;
      this.resetForLotChange();
    });
  }
}
